import * as React from 'react'
import { useNavigate } from 'react-router'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { createClientInputSchema } from '@lib/input/client.input'
import type { CreateClientInput } from '@lib/input/client.input'
import { CLIENT_TYPES, CLIENT_TYPE_LABELS } from '@lib/enums/client-type.enum'
import type { ClientType } from '@lib/enums/client-type.enum'
import { useCreateClient } from '@/hooks/useClientMutations'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { parseHomeCount } from '@/components/HomeCountControl'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Field-level messages, keyed by the fields this form actually submits.
 *
 * Keyed by `keyof CreateClientInput` rather than by `string`: a message stored
 * under a misspelled key used to be perfectly legal and simply never rendered,
 * which is the quietest possible way for validation to stop working. Renaming a
 * field in the shared schema now breaks this file at compile time instead.
 */
type ClientField = keyof CreateClientInput

type FieldErrors = Partial<Record<ClientField, string>>

/**
 * Zod reports an issue path as `PropertyKey[]` — it cannot know the path names
 * a field of ours. The schema's own shape is the authority on which names are
 * real, so the check is derived from it rather than from a hand-written list
 * that could drift from the schema it is meant to describe.
 */
const CLIENT_FIELDS = new Set<string>(Object.keys(createClientInputSchema.shape))

function isClientField(value: PropertyKey): value is ClientField {
  return typeof value === 'string' && CLIENT_FIELDS.has(value)
}

function createErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    return error.body.message ?? 'Could not create the client. Please try again.'
  }
  return toErrorMessage(error)
}

/**
 * Create a client on its own route rather than in a modal over the list.
 *
 * That follows the design system's list-to-create pattern: the list is the
 * resting state, and creating is a focused, cancellable task with room for the
 * address fields — which a dialog would cramp.
 *
 * The type choice comes first because it changes what the rest of the form
 * means: for a commercial client "Name" is an organization, for a residential
 * one it is a household.
 */
export function NewClientPage() {
  const navigate = useNavigate()
  const createClient = useCreateClient()

  const [type, setType] = React.useState<ClientType>('COMMERCIAL')
  const [name, setName] = React.useState<string>('')
  const [addressLine1, setAddressLine1] = React.useState<string>('')
  const [city, setCity] = React.useState<string>('')
  const [state, setState] = React.useState<string>('')
  const [postalCode, setPostalCode] = React.useState<string>('')
  const [notes, setNotes] = React.useState<string>('')
  const [homeCount, setHomeCount] = React.useState<string>('')

  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({})
  const [formError, setFormError] = React.useState<string | null>(null)

  const nameLabel = type === 'COMMERCIAL' ? 'Organization name' : 'Client name'
  const namePlaceholder =
    type === 'COMMERCIAL' ? 'The Trails at Regal Park' : 'Sam Grower'

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldErrors({})
    setFormError(null)

    const homes = parseHomeCount(type === 'COMMERCIAL' ? homeCount : '')
    if (!homes.ok) {
      setFieldErrors({ homeCount: homes.error })
      return
    }

    const parsed = createClientInputSchema.safeParse({
      name,
      type,
      addressLine1,
      city,
      state,
      postalCode,
      notes,
      homeCount: homes.value,
    })

    if (!parsed.success) {
      const next: FieldErrors = {}
      for (const issue of parsed.error.issues) {
        const field = issue.path[0]
        // First message per field wins: it is the one nearest the cause.
        if (field !== undefined && isClientField(field) && !next[field]) {
          next[field] = issue.message
        }
      }
      setFieldErrors(next)
      return
    }

    try {
      const client = await createClient.mutateAsync(parsed.data)
      navigate(`/clients/${client.id}`, { replace: true })
    } catch (caught) {
      setFormError(createErrorMessage(caught))
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <button
        type="button"
        onClick={() => navigate('/clients')}
        className="inline-flex items-center gap-1.5 text-sm text-muted-text hover:text-primary-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Clients
      </button>

      <div>
        <h1 className="text-xl font-semibold">New client</h1>
        <p className="mt-1 text-sm text-muted-text">
          Add the people and sites once the client exists.
        </p>
      </div>

      <form
        className="space-y-5 rounded-lg border border-default bg-surface p-4"
        onSubmit={handleSubmit}
        noValidate
      >
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-secondary-text">
            Type
          </legend>
          <div className="flex gap-2 pt-1">
            {CLIENT_TYPES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setType(option)}
                aria-pressed={type === option}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm transition-colors',
                  type === option
                    ? 'border-brand bg-brand-subtle font-medium text-primary-text'
                    : 'border-default text-secondary-text hover:border-strong hover:bg-subtle',
                )}
              >
                {CLIENT_TYPE_LABELS[option]}
              </button>
            ))}
          </div>
        </fieldset>

        <Field label={nameLabel} htmlFor="name" error={fieldErrors['name']}>
          <Input
            id="name"
            value={name}
            placeholder={namePlaceholder}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field
          label="Billing address"
          htmlFor="addressLine1"
          error={fieldErrors['addressLine1']}
          hint="Where invoices go. Service locations are added as sites."
        >
          <Input
            id="addressLine1"
            value={addressLine1}
            placeholder="1234 Kewanee Ave"
            onChange={(event) => setAddressLine1(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="City" htmlFor="city" error={fieldErrors['city']}>
            <Input
              id="city"
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
          </Field>
          <Field label="State" htmlFor="state" error={fieldErrors['state']}>
            <Input
              id="state"
              value={state}
              onChange={(event) => setState(event.target.value)}
            />
          </Field>
          <Field
            label="ZIP"
            htmlFor="postalCode"
            error={fieldErrors['postalCode']}
          >
            <Input
              id="postalCode"
              value={postalCode}
              onChange={(event) => setPostalCode(event.target.value)}
            />
          </Field>
        </div>

        {type === 'COMMERCIAL' ? (
          <Field
            label="Homes in the community"
            htmlFor="homeCount"
            error={fieldErrors['homeCount']}
            hint="Used for the price per home on proposals. Leave empty for a business that is not an association."
          >
            <Input
              id="homeCount"
              inputMode="numeric"
              className="max-w-32"
              value={homeCount}
              onChange={(event) => setHomeCount(event.target.value)}
            />
          </Field>
        ) : null}

        <Field label="Notes" htmlFor="notes" error={fieldErrors['notes']}>
          <textarea
            id="notes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="flex w-full rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none"
          />
        </Field>

        {formError ? (
          <p className="text-sm text-destructive">{formError}</p>
        ) : null}

        <div className="flex items-center gap-2 border-t border-default pt-4">
          <Button type="submit" disabled={createClient.isPending}>
            {createClient.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Create client
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/clients')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}

interface FieldProps {
  label: string
  htmlFor: string
  error?: string | undefined
  hint?: string
  children: React.ReactNode
}

/** Label + control + hint/error, so every field on the form lines up. */
function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-text">{hint}</p>
      ) : null}
    </div>
  )
}
